import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import getMetricSpec from "../utils/metrics";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import moment from "moment";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { useGetV4OrgReportByDate } from "../../perf_reports/api/organization/v4OrgReportByDate";
import { useGetV4OrgReportTotals } from "../../perf_reports/api/organization/v4OrgReportTotals";

const OrgPerformanceExpander = ({ row }) => {
  const acosOrRoas = useAcosOrRoas();

  const { org_id: orgId, org_name: orgName, market_place: marketPlace } = row._original ?? {};

  const { group } = useDates();

  const filters = useMemo(() => {
    if (!orgId) return undefined;
    return [
      {
        id: "org_id",
        value: { op: "=", value: orgId },
      },
    ];
  }, [orgId]);

  const getByDateOptionsVar = useGetV4OrgReportByDate(marketPlace);
  const getTotalsOptionsVar = useGetV4OrgReportTotals(marketPlace);

  const byDateQuery = useQuery(getByDateOptionsVar({ filters, search: undefined, allActive: true }));
  const totalsQuery = useQuery(getTotalsOptionsVar({ filters, search: undefined, allActive: true }));

  const isLoading = byDateQuery.isFetching || totalsQuery.isFetching || byDateQuery.isPending || totalsQuery.isPending;

  const dateRows = useMemo(() => byDateQuery.data?.data ?? [], [byDateQuery.data]);
  const totals = totalsQuery.data?.data;

  const metrics = useMemo(
    () => [
      getMetricSpec("sales"),
      getMetricSpec("units"),
      getMetricSpec("margin"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      getMetricSpec("roms"),
      getMetricSpec("cost"),
      getMetricSpec("promo_cost"),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["sales", "ad_sales", "ad_spend", "ctr"],
    "orgPerformanceExpander",
  );

  const dateRowsNormalized = useMemo(() => dateRows.map((d) => ({ ...d, date: d.start_date })), [dateRows]);

  const dateFilterWithoutToday = useMemo(
    () => (dateRowsNormalized ?? []).filter((d) => moment().format("YYYY-MM-DD") !== d.start_date),
    [dateRowsNormalized],
  );

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div>
      <Row>
        <Col xs={12}>
          <div style={{ display: "flex" }}>
            <div>
              <Row>
                <Col xs={12}>
                  <h3
                    style={{
                      marginBottom: "0.2rem",
                      marginTop: "0",
                      fontSize: 22,
                    }}
                  >
                    {orgName}
                  </h3>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        <Col xs={12}>
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={dateFilterWithoutToday}
            group={group}
            handleLegendChange={handleLegendChange}
            showAnnotations={false}
          />
        </Col>
        <Col xs={12}>
          <Row className="pt-4">
            {metrics?.slice(0, 6)?.map((m, i) => {
              return (
                <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                  <div className="">
                    <div>
                      <DetailedMetric center metric={m} data={{ total: totals }} showTarget={false} />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default OrgPerformanceExpander;
